import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';

import Box from '@redbubble/design-system/react/Box';

import { useRecentlyViewed } from '../../containers/apollo/withRecentlyViewed';
import { fragments as favoriteFragments } from '../../containers/apollo/withFavoriteStatuses';
import FavoritableItemCarousel, {
  fragments as carouselFragments
} from '../FavoritableItemCarousel';
import CarouselHeading from '../CarouselHeading';
import { analyticsPayload, RECENTLY_VIEWED_LABEL } from '../../lib/analytics';
import { useStrikethroughFeatureFlag } from '../../hooks';
import { AnalyticsItemsList } from '../../lib/gtm';

const messages = defineMessages({
  title: { defaultMessage: 'Recently Viewed' }
});

const RECENTLY_VIEWED_REQUIRED_ITEMS = 6;

const RecentlyViewedCarousel = ({
  logEvent,
  addImpression,
  logComponentInView,
  userInfo,
  profile,
  transparentCards,
  urlRef
}) => {
  const { country, currency, locale, isLoggedIn } = userInfo || {};
  const { items, loading } = useRecentlyViewed({
    country,
    currency,
    locale,
    fragments: [...carouselFragments, ...favoriteFragments]
  });

  const showStrikethroughPricing = useStrikethroughFeatureFlag();

  if (
    !loading &&
    (!Array.isArray(items) || items.length < RECENTLY_VIEWED_REQUIRED_ITEMS)
  )
    return null;

  return (
    <Box marginBottom={2}>
      <FormattedMessage {...messages.title}>
        {text => <CarouselHeading>{text}</CarouselHeading>}
      </FormattedMessage>
      <FavoritableItemCarousel
        isLoggedIn={isLoggedIn || false}
        profile={profile}
        loading={loading}
        items={items}
        rows={1}
        transparentCards={transparentCards}
        logImpression={(item, index) => {
          // eslint-disable-next-line no-underscore-dangle
          const id = item.__typename === 'Work' ? item.workId : item.id;
          const impression = analyticsPayload.impression(
            RECENTLY_VIEWED_LABEL,
            id,
            index + 1
          );
          addImpression(impression);
        }}
        onLoadingShowed={logComponentInView('explore-recently-viewed', true)}
        onContentShowed={logComponentInView('explore-recently-viewed', false)}
        requiredItems={RECENTLY_VIEWED_REQUIRED_ITEMS}
        onFavorite={(_, newState, index) => {
          logEvent({
            analytics: analyticsPayload.favorite({
              newState,
              rank: index + 1,
              source: 'explore-recently-viewed'
            })
          });
        }}
        urlRef={urlRef}
        showStrikethroughPricing={showStrikethroughPricing}
        analyticsComponentID={AnalyticsItemsList.RecentlyViewedCarousel}
      />
    </Box>
  );
};

RecentlyViewedCarousel.propTypes = {
  transparentCards: PropTypes.bool,
  urlRef: PropTypes.string
};

RecentlyViewedCarousel.defaultProps = {
  transparentCards: true,
  urlRef: null
};

export default RecentlyViewedCarousel;
